import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type TrialPlatform = "callback_zone" | "coach" | "castings" | "hmu" | "crew" | "capture" | "draftline";

interface TrialStatus {
  hasTrial: boolean;
  trialEndsAt: Date | null;
  daysRemaining: number | null;
  hadTrialBefore: boolean;
}

interface UseTrialStatusReturn {
  trialStatus: Record<TrialPlatform, TrialStatus>;
  isLoading: boolean;
  startTrial: (platform: TrialPlatform) => Promise<{ success: boolean; error?: string; trialEndsAt?: string }>;
  refetch: () => Promise<void>;
}

const defaultTrialStatus: TrialStatus = {
  hasTrial: false,
  trialEndsAt: null,
  daysRemaining: null,
  hadTrialBefore: false,
};

const ALL_PLATFORMS: TrialPlatform[] = [
  "callback_zone",
  "coach",
  "castings",
  "hmu",
  "crew",
  "capture",
  "draftline",
];

const emptyStatus = (): Record<TrialPlatform, TrialStatus> =>
  ALL_PLATFORMS.reduce(
    (acc, p) => ({ ...acc, [p]: defaultTrialStatus }),
    {} as Record<TrialPlatform, TrialStatus>,
  );

export const useTrialStatus = (): UseTrialStatusReturn => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: trialStatus = emptyStatus(), isLoading } = useQuery({
    queryKey: ["trial-status", user?.id ?? "anon"],
    enabled: !!user?.id,
    staleTime: 60_000,
    gcTime: 300_000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!user?.id) return emptyStatus();
      const results = await Promise.all(
        ALL_PLATFORMS.map(async (platform) => {
          const { data, error } = await supabase.rpc("get_trial_status", {
            p_user_id: user.id,
            p_platform: platform,
          });
          if (error) {
            return { platform, status: defaultTrialStatus };
          }
          const trialInfo = data?.[0];
          return {
            platform,
            status: {
              hasTrial: trialInfo?.has_trial ?? false,
              trialEndsAt: trialInfo?.trial_ends_at ? new Date(trialInfo.trial_ends_at) : null,
              daysRemaining: trialInfo?.days_remaining ?? null,
              hadTrialBefore: trialInfo?.had_trial_before ?? false,
            },
          };
        }),
      );
      return results.reduce(
        (acc, { platform, status }) => ({ ...acc, [platform]: status }),
        {} as Record<TrialPlatform, TrialStatus>,
      );
    },
  });

  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["trial-status", user?.id ?? "anon"] });
  }, [queryClient, user?.id]);

  const startTrial = useCallback(
    async (platform: TrialPlatform): Promise<{ success: boolean; error?: string; trialEndsAt?: string }> => {
      if (!user) {
        return { success: false, error: "You must be logged in to start a trial" };
      }

      try {
        const { data, error } = await supabase.functions.invoke("start-free-trial", {
          body: { platform },
        });

        if (error) {
          console.error("Error starting trial:", error);
          return { success: false, error: error.message || "Failed to start trial" };
        }

        if (data?.error) {
          return { success: false, error: data.error };
        }

        // Refetch trial status after starting
        await refetch();

        // Fire `start_trial` to GA4 + Meta. Single source of truth — every
        // CTA flows through this hook. Consent Mode v2 handles cookieless
        // transmission until the user grants analytics_storage.
        try {
          if (typeof window !== "undefined") {
            (window as any).gtag?.("event", "start_trial", {
              platform,
              plan_name: platform,
              billing_period: "trial",
            });
            (window as any).fbq?.("track", "StartTrial", {
              content_name: platform,
              predicted_ltv: 0,
              currency: "USD",
            });
          }
        } catch (e) {
          console.debug("[Analytics] start_trial failed", e);
        }

        return {
          success: true,
          trialEndsAt: data?.trial_ends_at,
        };
      } catch (err: any) {
        console.error("Error starting trial:", err);
        return { success: false, error: err.message || "Failed to start trial" };
      }
    },
    [user, refetch]
  );

  return {
    trialStatus,
    isLoading,
    startTrial,
    refetch,
  };
};

export default useTrialStatus;
